import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

/* Etiqueta — arquivo do shadcn, reestilizado para a Colmeia.
 *
 * `Badge` é a etiqueta GENÉRICA: contagem, categoria, "rascunho". Quando o
 * que se quer dizer é um JULGAMENTO ("abaixo do preço mínimo", "no ponto"),
 * o componente é `SeloEstado`, que tem marcador de célula e caixa alta.
 * Manter os dois separados evita que uma contagem pareça um veredito.
 *
 * As variantes semânticas usam a tinta translúcida (a cor a 12%) como fundo e
 * a cor cheia como texto, porque a cheia é a única que passa em contraste
 * sobre os seis campos claros. Dentro do campo mata as duas trocam de lado
 * sozinhas: as tintas ganham versão clara no escopo [data-campo='mata']. */

const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-pilula border border-transparent px-2.5 py-0.5 text-nota font-semibold whitespace-nowrap transition-[color,background-color] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-acao-forte",
        secondary: "bg-secondary text-secondary-foreground [a&]:hover:bg-areia-forte",
        destructive: "bg-destructive text-destructive-foreground [a&]:hover:brightness-110",
        outline: "border-input text-tinta-2 [a&]:hover:bg-lavado [a&]:hover:text-tinta",
        ghost: "text-tinta-2 [a&]:hover:bg-lavado [a&]:hover:text-tinta",
        link: "text-primary underline-offset-4 [a&]:hover:underline",

        // ── Variantes FAVO (acréscimo nosso ao arquivo do shadcn) ──────────
        positivo: "bg-positivo-tinta text-positivo",
        atencao: "bg-atencao-tinta text-atencao",
        negativo: "bg-negativo-tinta text-negativo",
        info: "bg-info-tinta text-info",
        neutro: "bg-lavado text-tinta-2",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span"

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
